import { useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Briefcase, User, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";

interface Props {
  open: boolean;
  onClose: () => void;
  managerSeats: { used: number; limit: number };
  staffSeats: { used: number; limit: number };
  agencyId: string | null;
  onInvited: () => void;
}

export default function InviteTeamModal({ open, onClose, managerSeats, staffSeats, agencyId, onInvited }: Props) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"manager" | "staff">("staff");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const { profile } = useProfile();
  const { user } = useAuth();

  if (!open) return null;

  const managerFull = managerSeats.used >= managerSeats.limit;
  const staffFull = staffSeats.used >= staffSeats.limit;

  async function send() {
    if (!email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (!agencyId || !user) {
      toast.error("No agency context");
      return;
    }
    setSending(true);
    try {
      // 1. Create invite via SECURITY DEFINER function — returns raw token ONCE.
      // The token is stored hashed; nobody (including agency owners) can read it back.
      const { data: result, error: inviteErr } = await (supabase as any).rpc("create_invite", {
        _email: email.trim().toLowerCase(),
        _role: role,
        _agency_id: agencyId,
      });
      if (inviteErr) throw inviteErr;
      if (!result?.ok) {
        // Server enforces the seat limit atomically — surface a friendly
        // upgrade prompt when we hit it.
        if (result?.error === "seat_limit_exceeded") {
          const roleLabel = (result?.role || role) === "manager" ? "manager" : "staff";
          toast.error(
            `You've reached the ${roleLabel} seat limit for your plan. Upgrade your plan to add more team members.`,
            {
              action: {
                label: "Upgrade",
                onClick: () => {
                  window.location.href = "/pricing";
                },
              },
            }
          );
          setSending(false);
          return;
        }
        throw new Error(result?.error || "Failed to create invite");
      }

      // 2. Build invite URL + send email
      const inviteUrl = `${window.location.origin}/signup?invite=${result.token}`;
      const inviterName = (profile as any)?.owner_name || (profile as any)?.agency_name || "Your team lead";
      const agencyName = (profile as any)?.agency_name || "your agency";
      const agencySegment = ((profile as any)?.segment === "grouphome" ? "grouphome" : "homecare") as "homecare" | "grouphome";

      const { error: fnErr } = await supabase.functions.invoke("send-staff-invite", {
        body: {
          inviteeEmail: email.trim(),
          inviterName,
          agencyName,
          agencySegment,
          role,
          personalMessage: note.trim() || null,
          inviteUrl,
          inviteExpiry: "72 hours",
          supportEmail: import.meta.env.VITE_SUPPORT_EMAIL || "support@example.com",
          unsubscribeUrl: `${window.location.origin}/unsubscribe`,
        },
      });
      if (fnErr) {
        // Email failed but invite row exists — still let owner share link manually
        toast.warning(`Invite created. Email failed: ${fnErr.message}`);
      } else {
        toast.success(`Invitation sent to ${email}`);
      }

      setEmail("");
      setNote("");
      onInvited();
      onClose();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to send invite");
    } finally {
      setSending(false);
    }
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-md bg-card rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-xl font-bold text-navy">Invite a Team Member</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <Label htmlFor="invite-email">Email Address</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="teammate@example.com"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="mb-2 block">Role</Label>
            <div className="grid grid-cols-2 gap-3">
              {/* Manager */}
              <button
                disabled={managerFull}
                onClick={() => !managerFull && setRole("manager")}
                className={`text-left rounded-xl border-2 p-4 transition-all ${
                  managerFull
                    ? "border-muted bg-muted/30 opacity-60 cursor-not-allowed"
                    : role === "manager"
                    ? "border-primary bg-primary/5"
                    : "border-border bg-muted/20 hover:border-muted-foreground/50 hover:bg-muted/40"
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  {managerFull ? <Lock className="h-4 w-4 text-muted-foreground" /> : <Briefcase className="h-4 w-4 text-primary" />}
                  <span className="font-bold text-navy text-sm">Manager</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Can view all leads, send outreach, manage referrals. No billing or analytics.
                </p>
                <p className="text-[11px] font-medium text-muted-foreground">
                  {managerFull
                    ? `Limit reached — upgrade for more.`
                    : `(${managerSeats.used}/${managerSeats.limit} used)`}
                </p>
              </button>

              {/* Staff */}
              <button
                disabled={staffFull}
                onClick={() => !staffFull && setRole("staff")}
                className={`text-left rounded-xl border-2 p-4 transition-all ${
                  staffFull
                    ? "border-muted bg-muted/30 opacity-60 cursor-not-allowed"
                    : role === "staff"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/50"
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  {staffFull ? <Lock className="h-4 w-4 text-muted-foreground" /> : <User className="h-4 w-4 text-muted-foreground" />}
                  <span className="font-bold text-navy text-sm">Staff Member</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Sees only assigned leads. Can update status and add notes.
                </p>
                <p className="text-[11px] font-medium text-muted-foreground">
                  {staffFull
                    ? `Limit reached — upgrade for more.`
                    : `(${staffSeats.used}/${staffSeats.limit} used)`}
                </p>
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="invite-note">Personal Message (optional)</Label>
            <Textarea
              id="invite-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              placeholder="Add a note to the invitation email..."
              className="mt-1"
            />
          </div>

          <Button
            onClick={send}
            disabled={sending}
            className="w-full bg-gold text-gold-foreground hover:bg-gold/90 font-bold min-h-[44px]"
          >
            {sending ? "Sending..." : "Send Invitation"}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            They will receive an email with a link to set their password and join your team. The invite expires in 72 hours.
          </p>
        </div>
      </div>
    </>,
    document.body
  );
}
